import { ActivityEntry } from '../components/activity-log/activity-log.types';

/**
 * The activity log's MERGE — REV-21378, ADR-0018 E4.
 *
 * Ported here from `aumico-be` when the service-to-service path was dropped (TL, 10.09.2026): nginx
 * already routes `/api/ai/**` to `ms-ai-agents`, so nothing server-side can see both halves and the
 * client is the only place that can. The logic and its claims are unchanged — only the process moved.
 *
 * Two sources, each served by its owner through the one origin the browser already uses:
 *
 *   - `AGENT_TRAIL`      — `ms-ai-agents`, fetched by REV-21869's pane
 *   - `CLOSING_ACTIVITY` — `aumico-be`, this ticket's half
 *
 * ⚠️ **The merge does NOT work on `ActivityEntry`, and it cannot.** That type carries `timeLabel` — a
 * DISPLAY string, relative for anything under a week ("vor 2 Stunden"). Display strings do not sort:
 * "vor 2 Stunden" against "24.04.2026 12:00" has no ordering, and formatting first would make the
 * list's order depend on how long ago things happened. So each source hands in {@link DatedEntry},
 * which keeps the raw ISO instant beside the already-mapped entry. `ActivityEntry` stays exactly as
 * REV-21366 accepted it.
 *
 * ⚠️ **A source may be UNAVAILABLE, and that is not the same as empty.** An empty log is a legitimate
 * answer (an account nothing has happened to); a failed request is not. `null` for a slice means "I
 * could not answer", and the result names it in `degraded` so the host can render the error state
 * instead of a half list that looks complete (AC-14).
 */

export type ActivitySourceName = 'AGENT_TRAIL' | 'CLOSING_ACTIVITY';

export const ACTIVITY_SOURCES: readonly ActivitySourceName[] = ['AGENT_TRAIL', 'CLOSING_ACTIVITY'];

/** One entry plus the instant it happened. The instant is what orders; the entry is what renders. */
export interface DatedEntry {
    /** ISO-8601 with offset, straight from the source. Never a formatted label. */
    readonly at: string;
    readonly entry: ActivityEntry;
}

/** What one source answered. `total` is that source's WHOLE count, not the slice's length. */
export interface ActivitySlice {
    readonly items: readonly DatedEntry[];
    readonly total: number;
    /** This source's own next position. `null` = that source is exhausted. */
    readonly nextCursor: string | null;
}

/** `null` = the source could not be read. Absent = the source was not asked. */
export type ActivitySlices = Partial<Record<ActivitySourceName, ActivitySlice | null>>;

export interface ActivityMerge {
    /** Newest first, capped at `limit`. */
    readonly entries: ActivityEntry[];
    /**
     * Dieselben Einträge, aber MIT ihrem rohen Zeitpunkt — die Form, in der sie sich noch sortieren
     * lassen.
     *
     * ⚠️ `ActivityEntry.timeLabel` ist ein Anzeigetext („vor 2 Stunden") und ordnet nicht. Wer eine
     * nachgeladene Seite an die bestehende Liste hängt, braucht `at`, sonst steht die neue Seite
     * unten — auch dann, wenn ihre Einträge JÜNGER sind als bereits angezeigte der anderen Hälfte.
     * Genau das passiert bei „Mehr laden": nur die menschliche Hälfte blättert weiter, der Trail
     * steht schon vollständig da, und D4 („neueste zuerst") galt danach nur noch für die erste Seite.
     */
    readonly dated: DatedEntry[];
    /** Sum over the sources that ANSWERED — never a count of what is on screen. */
    readonly totalCount: number;
    readonly canLoadMore: boolean;
    /** One position per source, to be handed back to that same source. */
    readonly cursors: Partial<Record<ActivitySourceName, string>>;
    /** Sources that could not be read. Empty on a complete page. */
    readonly degraded: ActivitySourceName[];
}

/**
 * Newest first, with a STABLE tiebreak.
 *
 * The tiebreak is not decoration. Two sources answer in two independent requests, so without it the
 * order of entries sharing an instant would depend on which response arrived first — i.e. on the
 * network. AC-9 promises the same surface renders the same order twice. `id` is unique across sources
 * by construction (`trail:…` / `act:…`), which is what makes it usable as the second key.
 */
function newestFirst(left: DatedEntry, right: DatedEntry): number {
    if (left.at !== right.at) {
        return left.at < right.at ? 1 : -1;
    }

    return left.entry.id < right.entry.id ? 1 : -1;
}

/**
 * Merge the slices into one page.
 *
 * `limit` caps what is DELIVERED, not what was fetched: entries beyond it are already in hand, so a
 * cursor is still owed for them — otherwise they would be unreachable and the list would silently
 * end early.
 */
export function mergeActivitySlices(slices: ActivitySlices, limit: number): ActivityMerge {
    const degraded: ActivitySourceName[] = [];
    const collected: DatedEntry[] = [];
    const cursors: Partial<Record<ActivitySourceName, string>> = {};
    let totalCount = 0;

    for (const source of ACTIVITY_SOURCES) {
        const slice = slices[source];
        if (slice === undefined) {
            continue;
        }
        if (slice === null) {
            degraded.push(source);
            continue;
        }

        collected.push(...slice.items);
        // Only sources that ANSWERED are counted. Adding an unreachable one as zero would make the
        // header read "this is all there is" about a list nobody could see.
        totalCount += slice.total;
        if (slice.nextCursor !== null) {
            cursors[source] = slice.nextCursor;
        }
    }

    const ordered = [...collected].sort(newestFirst);
    const delivered = ordered.slice(0, limit);

    // Truncated by the merge itself: the dropped items are the OLDEST of this page, so each source
    // resumes at its own last DELIVERED instant. Resuming both at one shared value is the trap — one
    // source would skip entries it never delivered, and they would be gone from the list for good.
    if (ordered.length > delivered.length) {
        for (const source of ACTIVITY_SOURCES) {
            const slice = slices[source];
            if (slice === undefined || slice === null) {
                continue;
            }
            const lastDelivered = [...delivered]
                .reverse()
                .find((item) => slice.items.some((candidate) => candidate.entry.id === item.entry.id));
            if (lastDelivered !== undefined) {
                cursors[source] = lastDelivered.at;
            }
        }
    }

    return {
        entries: delivered.map((item) => item.entry),
        dated: delivered,
        totalCount,
        canLoadMore: Object.keys(cursors).length > 0,
        cursors,
        degraded,
    };
}

/**
 * Append a newly loaded page to what is already on screen.
 *
 * APPEND, not replace (AC-10) — and de-duplicating by id rather than trusting the cursor, because a
 * boundary entry is exactly what an off-by-one cursor returns twice, and the symptom would be a
 * duplicated row rather than an error.
 */
export function appendActivityEntries(existing: readonly DatedEntry[], incoming: readonly DatedEntry[]): DatedEntry[] {
    const seen = new Set(existing.map((item) => item.entry.id));

    // ⚠️ NACH dem Anhängen neu sortiert, nicht nur angehängt. „Mehr laden" blättert allein die
    // menschliche Hälfte weiter, während der Trail bereits vollständig in `existing` steht; eine neue
    // Menschen-Seite kann deshalb Einträge enthalten, die JÜNGER sind als schon angezeigte
    // Trail-Einträge. Ohne diese Zeile stünden sie darunter, und die Liste wäre nach dem ersten
    // Nachladen nicht mehr durchgehend „neueste zuerst" (D4).
    return [...existing, ...incoming.filter((item) => !seen.has(item.entry.id))].sort(newestFirst);
}
